import { useState, useEffect, useCallback } from 'react';
import logger from '../lib/logger';
import { useAuth } from '../context/AuthContext';
import { servicesAPI, serviceCategoriesAPI } from '../lib/api';
import { formatCurrency, cn } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Search, 
  Plus, 
  Scissors, 
  Clock,
  Edit,
  Trash2,
  Loader2,
  Settings,
  Check,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

export default function ServiciosPage() {
  const { selectedBusiness, user } = useAuth();
  const canManageCategories = !!user?.is_master_admin;
  const canCreateCategories = canManageCategories || user?.role === 'admin';

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  // Persisted categories from backend
  const [categoryDocs, setCategoryDocs] = useState([]);
  const [addingCategory, setAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [savingCategory, setSavingCategory] = useState(false);
  const [manageCatsOpen, setManageCatsOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    duration_minutes: 30,
    price: 0,
    category: '',
    commission_rate: null,
    commission_fixed: null,
    commission_base: null
  });

  const loadServices = useCallback(async () => {
    if (!selectedBusiness) return;
    
    try {
      setLoading(true);
      const response = await servicesAPI.getAll({ active_only: false });
      setServices(response.data);
    } catch (error) {
      logger.error('Error loading services:', error);
      toast.error('Error al cargar servicios');
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness]);

  const loadCategories = useCallback(async () => {
    if (!selectedBusiness) return;
    try {
      const { data } = await serviceCategoriesAPI.list();
      setCategoryDocs(data?.categories || []);
    } catch (error) {
      logger.error('Error loading service categories:', error);
    }
  }, [selectedBusiness]);

  useEffect(() => {
    loadServices();
    loadCategories();
  }, [loadServices, loadCategories]);

  const handleCreateCategory = async () => {
    const name = newCategoryName.trim();
    if (name.length < 2) {
      toast.error('El nombre debe tener al menos 2 caracteres');
      return;
    }
    setSavingCategory(true);
    try {
      const { data } = await serviceCategoriesAPI.create(name);
      toast.success(`Categoría "${data.category.name}" agregada`);
      setCategoryDocs((prev) => [...prev, data.category].sort((a, b) => a.name.localeCompare(b.name)));
      setFormData((prev) => ({ ...prev, category: data.category.name }));
      setNewCategoryName('');
      setAddingCategory(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo crear la categoría');
    } finally {
      setSavingCategory(false);
    }
  };

  const handleDeleteCategory = async (cat) => {
    if (!window.confirm(`¿Eliminar la categoría "${cat.name}"?`)) return;
    try {
      await serviceCategoriesAPI.delete(cat.id);
      toast.success(`Categoría "${cat.name}" eliminada`);
      setCategoryDocs((prev) => prev.filter((c) => c.id !== cat.id));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  const openNewService = () => {
    setSelectedService(null);
    setFormData({
      name: '',
      description: '',
      duration_minutes: 30,
      price: 0,
      category: '',
      commission_rate: null,
      commission_fixed: null,
      commission_base: null
    });
    setDialogOpen(true);
  };

  const openEditService = (service) => {
    setSelectedService(service);
    setFormData({
      name: service.name,
      description: service.description || '',
      duration_minutes: service.duration_minutes,
      price: service.price,
      category: service.category || '',
      commission_rate: service.commission_rate,
      commission_fixed: service.commission_fixed,
      commission_base: service.commission_base ?? null
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || formData.price < 0) {
      toast.error('Nombre y precio son requeridos');
      return;
    }

    try {
      if (selectedService) {
        await servicesAPI.update(selectedService.id, formData);
        toast.success('Servicio actualizado');
      } else {
        await servicesAPI.create({
          ...formData,
          business_id: selectedBusiness.id
        });
        toast.success('Servicio creado');
      }
      setDialogOpen(false);
      loadServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (service) => {
    if (!window.confirm('¿Estás seguro de eliminar este servicio?')) return;
    
    try {
      await servicesAPI.delete(service.id);
      toast.success('Servicio eliminado');
      loadServices();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleToggleActive = async (service) => {
    try {
      await servicesAPI.update(service.id, { is_active: !service.is_active });
      toast.success(service.is_active ? 'Servicio desactivado' : 'Servicio activado');
      loadServices();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const filteredServices = services.filter(s => 
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Merge persisted category names with any legacy category still present on a
  // service (deduped, case-insensitive) so the dropdown never loses an option
  // in an edge case where the backend seed missed it.
  const persistedNames = categoryDocs.map((c) => c.name);
  const legacyOnly = [...new Set(services.map((s) => s.category).filter(Boolean))]
    .filter((n) => !persistedNames.some((p) => p.toLowerCase() === n.toLowerCase()));
  const categoryOptions = [...persistedNames, ...legacyOnly].sort((a, b) => a.localeCompare(b));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="servicios-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Servicios
          </h1>
          <p className="text-slate-500 mt-1">
            {services.filter(s => s.is_active).length} servicios activos
          </p>
        </div>
        <Button 
          onClick={openNewService}
          className="bg-primary text-white hover:bg-primary/90"
          data-testid="new-service-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo servicio
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Buscar servicios..."
          className="pl-10 bg-white border-slate-200"
        />
      </div>

      {/* Services Table */}
      <Card className="border-slate-200 bg-white">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
            </div>
          ) : filteredServices.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500">
              <Scissors className="w-12 h-12 mb-3 opacity-50" />
              <p>No hay servicios</p>
              <Button variant="link" onClick={openNewService} className="mt-2">
                Agregar primer servicio
              </Button>
            </div>
          ) : (
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Servicio</TableHead>
                  <TableHead>Categoría</TableHead>
                  <TableHead>Duración</TableHead>
                  <TableHead>Precio</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredServices.map((service, index) => (
                  <TableRow 
                    key={service.id}
                    className={cn(
                      "animate-fade-in",
                      !service.is_active && "opacity-50"
                    )}
                    style={{ animationDelay: `${index * 0.03}s` }}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center">
                          <Scissors className="w-5 h-5 text-slate-500" />
                        </div>
                        <div>
                          <p className="font-medium">{service.name}</p>
                          {service.description && (
                            <p className="text-xs text-slate-500 truncate max-w-[200px]">
                              {service.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-slate-500 border-slate-200">
                        {service.category || 'Sin categoría'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-slate-500">
                        <Clock className="w-4 h-4" />
                        {service.duration_minutes} min
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-bold text-lg" style={{ fontFamily: 'Manrope' }}>
                        {formatCurrency(service.price)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant="outline" 
                        className={cn(
                          "cursor-pointer",
                          service.is_active 
                            ? "text-emerald-400 border-emerald-500/30" 
                            : "text-slate-500 border-slate-200"
                        )}
                        onClick={() => handleToggleActive(service)}
                      >
                        {service.is_active ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8 hover:bg-slate-100"
                          onClick={() => openEditService(service)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8 hover:bg-slate-100 hover:text-red-400"
                          onClick={() => handleDelete(service)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Service Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-lg max-h-[90vh] overflow-y-auto scrollbar-visible">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              {selectedService ? 'Editar servicio' : 'Nuevo servicio'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-slate-100 border-slate-200"
                placeholder="Ej: Corte clásico"
              />
            </div>

            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-slate-100 border-slate-200 resize-none"
                placeholder="Descripción del servicio..."
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Categoría</Label>
                  {canManageCategories && (
                    <button
                      type="button"
                      className="text-[11px] text-slate-500 hover:text-primary inline-flex items-center gap-1"
                      onClick={() => setManageCatsOpen(true)}
                      data-testid="manage-categories-btn"
                    >
                      <Settings className="w-3 h-3" /> Administrar
                    </button>
                  )}
                </div>
                {addingCategory ? (
                  <div className="flex gap-1" data-testid="new-category-inline">
                    <Input
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); handleCreateCategory(); }
                        if (e.key === 'Escape') { setAddingCategory(false); setNewCategoryName(''); }
                      }}
                      className="bg-slate-100 border-slate-200 h-9"
                      placeholder="Nombre de la categoría"
                      autoFocus
                      data-testid="new-category-name-input"
                    />
                    <Button
                      type="button"
                      size="icon"
                      className="h-9 w-9 bg-emerald-600 hover:bg-emerald-700 text-white flex-shrink-0"
                      onClick={handleCreateCategory}
                      disabled={savingCategory}
                      data-testid="new-category-save-btn"
                    >
                      {savingCategory ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      className="h-9 w-9 flex-shrink-0"
                      onClick={() => { setAddingCategory(false); setNewCategoryName(''); }}
                      data-testid="new-category-cancel-btn"
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ) : (
                  <Select
                    value={formData.category || undefined}
                    onValueChange={(value) => {
                      if (value === '__add__') {
                        if (!canCreateCategories) {
                          toast.error('Sin permisos para crear categorías');
                          return;
                        }
                        setAddingCategory(true);
                        return;
                      }
                      setFormData({ ...formData, category: value });
                    }}
                  >
                    <SelectTrigger
                      className="bg-slate-100 border-slate-200"
                      data-testid="category-select-trigger"
                    >
                      <SelectValue placeholder="Sin categoría" />
                    </SelectTrigger>
                    <SelectContent>
                      {categoryOptions.map((cat) => (
                        <SelectItem key={cat} value={cat} data-testid={`category-option-${cat}`}>
                          {cat}
                        </SelectItem>
                      ))}
                      {categoryOptions.length === 0 && (
                        <div className="px-2 py-1.5 text-xs text-slate-400">
                          Aún no hay categorías
                        </div>
                      )}
                      {canCreateCategories && (
                        <>
                          <div className="my-1 h-px bg-slate-200" />
                          <SelectItem
                            value="__add__"
                            className="text-primary font-medium"
                            data-testid="category-option-add-new"
                          >
                            <span className="inline-flex items-center gap-1.5">
                              <Plus className="w-3.5 h-3.5" /> Nueva categoría…
                            </span>
                          </SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div className="space-y-2">
                <Label>Duración (minutos)</Label>
                <Input
                  type="number"
                  value={formData.duration_minutes}
                  onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 30 })}
                  className="bg-slate-100 border-slate-200"
                  min={5}
                  step={5}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Precio *</Label>
              <Input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                className="bg-slate-100 border-slate-200"
                min={0}
                step={0.01}
              />
            </div>

            <div className="space-y-2">
              <Label>Costo base para comisión</Label>
              <Input
                type="number"
                value={formData.commission_base ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setFormData({
                    ...formData,
                    commission_base: v === '' ? null : parseFloat(v) || 0,
                  });
                }}
                className="bg-slate-100 border-slate-200"
                min={0}
                step={0.01}
                placeholder="Se usa el precio si se deja vacío"
                data-testid="service-commission-base-input"
              />
              <p className="text-[11px] text-slate-500">
                Base interna que se usa <strong>solo</strong> para calcular la comisión del barbero.
                Si queda vacío, la comisión se calcula sobre el precio de venta.
              </p>
            </div>

            <div className="pt-2 border-t border-slate-200">
              <p className="text-sm text-slate-500 mb-3">Comisiones (opcional - sobrescribe la del barbero)</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Comisión (%)</Label>
                  <Input
                    type="number"
                    value={formData.commission_rate || ''}
                    onChange={(e) => setFormData({ ...formData, commission_rate: parseFloat(e.target.value) || null })}
                    className="bg-slate-100 border-slate-200"
                    min={0}
                    max={100}
                    placeholder="Ej: 30"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Comisión fija ($)</Label>
                  <Input
                    type="number"
                    value={formData.commission_fixed || ''}
                    onChange={(e) => setFormData({ ...formData, commission_fixed: parseFloat(e.target.value) || null })}
                    className="bg-slate-100 border-slate-200"
                    min={0}
                    placeholder="Ej: 50"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-200">
              Cancelar
            </Button>
            <Button onClick={handleSubmit} className="bg-primary text-white hover:bg-primary/90">
              {selectedService ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage categories dialog (master_admin only) */}
      <Dialog open={manageCatsOpen} onOpenChange={setManageCatsOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" /> Administrar categorías
            </DialogTitle>
            <DialogDescription>
              Elimina categorías que ya no uses. Las que estén en uso no se pueden eliminar hasta reasignar los servicios.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[60vh] overflow-y-auto -mx-2 px-2">
            {categoryDocs.length === 0 ? (
              <div className="py-8 text-center text-slate-400 text-sm">
                No hay categorías registradas
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Categoría</TableHead>
                    <TableHead className="text-center">Servicios</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categoryDocs.map((cat) => (
                    <TableRow key={cat.id} data-testid={`manage-cat-row-${cat.id}`}>
                      <TableCell className="font-medium">{cat.name}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="text-slate-600 border-slate-200">
                          {cat.usage_count || 0}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-red-500 hover:bg-red-50"
                          onClick={() => handleDeleteCategory(cat)}
                          disabled={cat.usage_count > 0}
                          title={cat.usage_count > 0 ? 'En uso — no se puede eliminar' : 'Eliminar categoría'}
                          data-testid={`delete-cat-${cat.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setManageCatsOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
